import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DEVICE_CONFIG from '@shared/config';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const EXPORT_RESOLUTIONS = {
  iphone_1242x2688: [1242, 2688],
  iphone_2688x1242: [2688, 1242],
  iphone_1284x2778: [1284, 2778],
  iphone_2778x1284: [2778, 1284],
  ipad_2064x2752: [2064, 2752],
  ipad_2752x2064: [2752, 2064],
  ipad_2048x2732: [2048, 2732],
  ipad_2732x2048: [2732, 2048],
};

const PreviewCanvas = ({
  outputs,
  activeDevice,
  onActiveDevice,
  zoom,
  onZoom,
  sourceImage,
  draft,
  device,
  onUpdateDraft,
}) => {
  const [sourceUrl, setSourceUrl] = useState('');
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [panGrab, setPanGrab] = useState(null);
  const [frameGrab, setFrameGrab] = useState(null);
  const [textGrab, setTextGrab] = useState(null);
  const liveRef = useRef(null);
  const generated = outputs[activeDevice];
  const frame = draft.framePosition || { x: 7, y: 24 };
  const frameSize = draft.frameSize || { width: 86, height: 76 };
  const deviceConfig = DEVICE_CONFIG[device] || DEVICE_CONFIG.iphone;
  const [exportWidth, exportHeight] = EXPORT_RESOLUTIONS[draft.exportPreset] || [
    deviceConfig.width,
    deviceConfig.height,
  ];

  useEffect(() => {
    if (!sourceImage) {
      setSourceUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(sourceImage);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [sourceImage]);
  useEffect(() => {
    if (liveRef.current) {
      liveRef.current.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
      liveRef.current.style.transformOrigin = 'center';
      liveRef.current.style.aspectRatio = `${exportWidth} / ${exportHeight}`;
    }
  }, [zoom, pan, sourceUrl, exportWidth, exportHeight]);
  useEffect(() => {
    if (!liveRef.current) return;
    const displayScale = Math.min(360, window.innerWidth * 0.7) / exportWidth;
    const heading = liveRef.current.querySelector('.live-heading');
    const subheading = liveRef.current.querySelector('.live-subheading');
    if (heading)
      heading.style.fontSize = `${Math.max(8, draft.headingSize * 16 * displayScale)}px`;
    if (subheading)
      subheading.style.fontSize = `${Math.max(7, draft.subheadingSize * 16 * displayScale)}px`;
  }, [sourceUrl, exportWidth, draft.headingSize, draft.subheadingSize]);

  const startPan = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setPanGrab({ x: event.clientX - pan.x, y: event.clientY - pan.y });
  };
  const movePan = (event) => {
    if (panGrab) setPan({ x: event.clientX - panGrab.x, y: event.clientY - panGrab.y });
  };
  const stopPan = () => setPanGrab(null);
  const updateFrameSize = (patch) =>
    onUpdateDraft({ frameSize: { ...frameSize, ...patch } });
  const startFrame = (event) => {
    event.stopPropagation();
    const box = liveRef.current.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    setFrameGrab({
      x: ((event.clientX - box.left) / box.width) * 100 - frame.x,
      y: ((event.clientY - box.top) / box.height) * 100 - frame.y,
    });
  };
  const moveFrame = (event) => {
    if ((!frameGrab && !textGrab) || !liveRef.current) return;
    const box = liveRef.current.getBoundingClientRect();
    if (textGrab) {
      onUpdateDraft({
        textPositions: {
          ...draft.textPositions,
          [textGrab.id]: {
            ...draft.textPositions[textGrab.id],
            x: clamp(((event.clientX - box.left) / box.width) * 100 - textGrab.x, 0, 100),
            y: clamp(((event.clientY - box.top) / box.height) * 100 - textGrab.y, 0, 45),
          },
        },
      });
      return;
    }
    onUpdateDraft({
      framePosition: {
        x: clamp(
          ((event.clientX - box.left) / box.width) * 100 - frameGrab.x,
          -(frameSize.width / 2),
          100 - frameSize.width / 2,
        ),
        y: clamp(
          ((event.clientY - box.top) / box.height) * 100 - frameGrab.y,
          -(frameSize.height / 2),
          100 - frameSize.height / 2,
        ),
      },
    });
  };
  const startText = (id) => (event) => {
    event.stopPropagation();
    const box = liveRef.current.getBoundingClientRect();
    const position = draft.textPositions[id];
    event.currentTarget.setPointerCapture(event.pointerId);
    setTextGrab({
      id,
      x: ((event.clientX - box.left) / box.width) * 100 - position.x,
      y: ((event.clientY - box.top) / box.height) * 100 - position.y,
    });
  };

  return (
    <div className="preview-area">
      <div className="preview-toolbar">
        <div className="output-tabs">
          {Object.keys(outputs).map((device) => (
            <button
              type="button"
              key={device}
              className={activeDevice === device ? 'active' : ''}
              onClick={() => onActiveDevice(device)}
            >
              {device}
            </button>
          ))}
        </div>
        <div className="zoom-controls">
          <button type="button" onClick={() => onZoom(Math.max(0.35, zoom - 0.15))}>
            −
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => onZoom(Math.min(2.5, zoom + 0.15))}>
            +
          </button>
          <button
            type="button"
            onClick={() => {
              setPan({ x: 0, y: 0 });
              onZoom(1);
            }}
          >
            <i className="bi bi-aspect-ratio" />
          </button>
        </div>
      </div>
      <div
        className="canvas-viewport"
        onPointerDown={!generated ? startPan : undefined}
        onPointerMove={(event) => {
          if (generated) movePan(event);
          else {
            movePan(event);
            moveFrame(event);
          }
        }}
        onPointerUp={() => {
          stopPan();
          setFrameGrab(null);
          setTextGrab(null);
        }}
      >
        {generated ? (
          <div
            className="canvas-art generated-art"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
            onPointerDown={startPan}
          >
            <img src={generated} alt="Generated preview" draggable="false" />
          </div>
        ) : sourceUrl ? (
          <div
            ref={liveRef}
            className="live-art"
            onPointerMove={moveFrame}
            onPointerUp={() => {
              setFrameGrab(null);
              setTextGrab(null);
            }}
            style={{
              background: `linear-gradient(135deg, ${draft.primary}, ${draft.secondary})`,
              aspectRatio: `${deviceConfig.width} / ${deviceConfig.height}`,
            }}
          >
            <div
              className="live-heading editable-text"
              style={{
                left: `${draft.textPositions.heading.x}%`,
                top: `${draft.textPositions.heading.y}%`,
                fontSize: `${Math.max(14, draft.headingSize * 4)}px`,
              }}
              onPointerDown={startText('heading')}
            >
              {draft.heading}
            </div>
            <div
              className="live-subheading editable-text"
              style={{
                left: `${draft.textPositions.subheading.x}%`,
                top: `${draft.textPositions.subheading.y}%`,
                fontSize: `${Math.max(10, draft.subheadingSize * 4)}px`,
              }}
              onPointerDown={startText('subheading')}
            >
              {draft.subheading}
            </div>
            <div
              className="live-frame-tools"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <label>
                W
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={frameSize.width}
                  onChange={(event) =>
                    updateFrameSize({ width: Number(event.target.value) })
                  }
                />
              </label>
              <label>
                H
                <input
                  type="range"
                  min="30"
                  max="100"
                  value={frameSize.height}
                  onChange={(event) =>
                    updateFrameSize({ height: Number(event.target.value) })
                  }
                />
              </label>
            </div>
            <div
              className="live-frame"
              style={{
                left: `${frame.x}%`,
                top: `${frame.y}%`,
                width: `${frameSize.width}%`,
                height: `${frameSize.height}%`,
              }}
              onPointerDown={startFrame}
            >
              <img
                src={sourceUrl}
                alt="Live upload preview"
                draggable="false"
                style={{
                  objectFit: draft.cropFit,
                  objectPosition: `${draft.imagePosition.x}% ${draft.imagePosition.y}%`,
                  transform: `rotate(${draft.imageRotation}deg) scale(${draft.imageZoom})`,
                }}
              />
            </div>
          </div>
        ) : (
          <div className="empty-preview">
            <h3>Your canvas is ready</h3>
            <p>Upload an image to start editing.</p>
          </div>
        )}
      </div>
    </div>
  );
};

PreviewCanvas.propTypes = {
  outputs: PropTypes.object.isRequired,
  activeDevice: PropTypes.string.isRequired,
  onActiveDevice: PropTypes.func.isRequired,
  zoom: PropTypes.number.isRequired,
  onZoom: PropTypes.func.isRequired,
  sourceImage: PropTypes.object,
  draft: PropTypes.object.isRequired,
  device: PropTypes.string.isRequired,
  onUpdateDraft: PropTypes.func.isRequired,
};
export default PreviewCanvas;
